/**
 * Who can see a thing, as a per-item chip (TMMAPP-0150 U2; TMMAPP-0151 §A 1). One rule, written once:
 * a virtual account's own space (`spacePrivacy`) is "Private · only you" when it's yours and
 * unshared, "Shared with {names}" when it's yours and shared (`sharedWithNames`, TMMAPI-0186 U3),
 * and "{owner}'s · shared with you" in lavender when it's someone else's. The cards and rows that
 * list virtual accounts carry it (`ToneChip`).
 *
 * TMMAPP-0161 item 3 (Operator, 2026-10-20): the top bar and the page titles carry no privacy pill;
 * only the per-item chips say who can see each thing.
 */
import type { TFunction } from 'i18next';
import type { MeVirtualAccount } from '@/api/me';
import { formatList } from '@/i18n/format';
import { personName } from '@/i18n/personName';

/** A chip's tones (visual contract v2 §8): who can see the thing. */
export type PrivacyTone = 'private' | 'shared' | 'partner' | 'community';

export interface PrivacyChipContent {
  tone: PrivacyTone;
  /** The chip's text, from the catalog ("Shared with Dana"). */
  label: string;
}

/** A virtual account's own space: private, shared with the people named, or someone else's. */
export function spacePrivacy(
  virtualAccount: Pick<
    MeVirtualAccount,
    'mine' | 'sharedWithNames' | 'ownerDisplayName' | 'sharedBy'
  >,
  t: TFunction,
  locale: string,
): PrivacyChipContent {
  if (!virtualAccount.mine) {
    return {
      tone: 'partner',
      label: t('shell.chipSharedWithYou', {
        owner: personName(virtualAccount.ownerDisplayName ?? virtualAccount.sharedBy, t),
      }),
    };
  }
  if (virtualAccount.sharedWithNames.length === 0) {
    return { tone: 'private', label: t('shell.chipPrivate') };
  }
  return {
    tone: 'shared',
    label: t('shell.chipSharedWith', { names: formatList(virtualAccount.sharedWithNames, locale) }),
  };
}

const VIRTUAL_ACCOUNT_ROUTE = /^\/v\/(\d+)(?:\/|$)/;

/** The virtual account id a `/v/:virtualAccountId/…` route names, else null (the top bar's people). */
export function routeVirtualAccountId(pathname: string): number | null {
  const match = VIRTUAL_ACCOUNT_ROUTE.exec(pathname);
  if (match === null) return null;
  const id = Number(match[1]);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}
